import { Dispatch, useReducer } from "react";
import { FluxStandardAction } from "typesafe-actions";

import IUserMe, { TTermsAndConditions } from "@appInterfaces/users/me";
import IProfileData, {
  INotifications,
} from "@profile/pages/AccountPage/IProfileData";
import {
  TAccountData,
  TModalForm,
} from "@profile/pages/AccountPage/IAccountPage";

import {
  setProfileDataAfterEmailUpdate,
  updateProfileDataTransformator,
} from "./helpers/reducer";
import { objectNullValuesToEmptyStrings } from "@features/utils/serverResponse";

export type TProfileData = keyof IProfileData;

enum EActionType {
  FETCH_INIT = "FETCH_INIT",
  FETCH_SUCCESS = "FETCH_SUCCESS",
  FETCH_FAILURE = "FETCH_FAILURE",
  MODAL_UPDATE = "MODAL_UPDATE",
  MODAL_CLOSE = "MODAL_CLOSE",
  CHANGE_EMAIL = "CHANGE_EMAIL",
  PROFILE_DATA_SET = "PROFILE_DATA/SET",
  PROFILE_DATA_UPDATE = "PROFILE_DATA/UPDATE",
  SET_CLOSE_BUTTON_VISIBILITY = "SET_CLOSE_BUTTON_VISIBILITY",
  EMAIL_NOTIFICATION_DATA_UPDATE = "EMAIL_NOTIFICATION_DATA_UPDATE",
  EMAIL_NOTIFICATION_DATA_SYNC = "EMAIL_NOTIFICATION_DATA_SYNC",
  SET_MODAL_ERROR = "SET_MODAL_ERROR",
}

export interface IProfileDataUpdate {
  path: (string | TProfileData)[];
  value: string | boolean;
}

export interface IState {
  profileData: IProfileData | null;
  profileDataUpdate: IProfileData | null;
  emailNotificationData: INotifications | null;
  emailNotificationDataUpdate: INotifications | null;
  modalForm: TModalForm;
  accountData: TAccountData;
  isLoading: boolean;
  _2fa_set: boolean;
  canCloseAccount: boolean;
  isInitialize: boolean;
  shouldRenderCloseButton: boolean;
  modalError: string;
  created_at?: string;
  terms_and_conditions: TTermsAndConditions[];
  isOrganization?: boolean;
}

type TAction = FluxStandardAction<
  string,
  {
    profileData?: IProfileData;
    userData?: IUserMe;
    canCloseAccount?: boolean;
    modalForm?: TModalForm;
    accountData?: TAccountData;
    profileDataUpdate?: IProfileDataUpdate;
    closeButtonVisible?: boolean;
    emailNotificationData?: { newsletter_enabled: boolean };
    modalErrorMessage?: string;
  }
>;

const dataFetchReducer = (state: IState, action: TAction): IState => {
  switch (action.type) {
    case EActionType.FETCH_INIT:
      return {
        ...state,
        isLoading: true,
      };
    case EActionType.FETCH_SUCCESS:
      const data = objectNullValuesToEmptyStrings(
        action!.payload!.profileData!
      ) as IProfileData;

      const emailNotificationData = {
        newsletter: action.payload!.emailNotificationData!.newsletter_enabled,
      };

      return {
        ...state,
        emailNotificationData,
        emailNotificationDataUpdate: emailNotificationData,
        isLoading: false,
        profileData: data,
        profileDataUpdate: data,
        _2fa_set: action!.payload!.userData!["2fa_enabled"],
        created_at: action!.payload!.userData!.created_at,
        isOrganization: action!.payload!.userData!.organizational,
        terms_and_conditions:
          action!.payload!.userData!.terms_and_conditions ?? [],
        canCloseAccount: action!.payload!.canCloseAccount!,
      };
    case EActionType.FETCH_FAILURE:
      return {
        ...state,
        isLoading: false,
      };
    case EActionType.MODAL_UPDATE:
      return {
        ...state,
        modalForm: action!.payload!.modalForm!,
        accountData: action!.payload!.accountData || state.accountData,
      };
    case EActionType.SET_CLOSE_BUTTON_VISIBILITY:
      return {
        ...state,
        shouldRenderCloseButton: Boolean(action.payload!.closeButtonVisible),
      };
    case EActionType.MODAL_CLOSE:
      return {
        ...state,
        modalForm: initialModalForm,
        accountData: initialAccountData,
      };
    case EActionType.CHANGE_EMAIL:
      const profileData = setProfileDataAfterEmailUpdate(
        state.profileData,
        action!.payload!.accountData!.data.email
      );

      return {
        ...state,
        profileData,
        modalForm: action!.payload!.modalForm!,
        accountData: initialAccountData,
      };
    case EActionType.PROFILE_DATA_SET:
      return {
        ...state,
        profileData: action!.payload!.profileData!,
        profileDataUpdate: action!.payload!.profileData!,
      };
    case EActionType.PROFILE_DATA_UPDATE:
      const actionData = action!.payload!.profileDataUpdate!;

      return updateProfileDataTransformator(state, actionData);
    case EActionType.EMAIL_NOTIFICATION_DATA_UPDATE:
      return {
        ...state,
        isInitialize: true,
        emailNotificationDataUpdate: {
          newsletter: action.payload!.emailNotificationData!.newsletter_enabled,
        },
      };
    case EActionType.EMAIL_NOTIFICATION_DATA_SYNC:
      return {
        ...state,
        emailNotificationData: {
          newsletter: state.emailNotificationDataUpdate!.newsletter,
        },
      };
    case EActionType.SET_MODAL_ERROR:
      return {
        ...state,
        modalError: action.payload!.modalErrorMessage!,
      };
    default:
      return state;
  }
};

const initialModalForm = {
  loader: false,
  type: null,
  header: "",
  isVisible: false,
  description: "",
  fields: [],
};

const initialAccountData = {
  data: {
    email: "",
    old_password: "",
    new_password: "",
    otp_code: "",
    password_confirmation: "",
  },
  validation: {},
};

export const useFetch = (): [IState, Dispatch<TAction>] => {
  return useReducer(dataFetchReducer, {
    profileData: null,
    profileDataUpdate: null,
    emailNotificationData: null,
    emailNotificationDataUpdate: null,
    modalForm: initialModalForm,
    accountData: initialAccountData,
    isLoading: false,
    _2fa_set: false,
    canCloseAccount: false,
    isInitialize: false,
    shouldRenderCloseButton: true,
    modalError: "",
    terms_and_conditions: [],
  });
};

export const initFetch = (): TAction => ({ type: EActionType.FETCH_INIT });

export const onFetchSuccess = (
  profileData: IProfileData,
  userData: IUserMe,
  canCloseAccount: boolean,
  emailNotificationData?: { newsletter_enabled: boolean }
): TAction => ({
  type: EActionType.FETCH_SUCCESS,
  payload: { profileData, userData, canCloseAccount, emailNotificationData },
});

export const updateFormModal = (
  modalForm: TModalForm,
  accountData?: TAccountData
): TAction => ({
  type: EActionType.MODAL_UPDATE,
  payload: { modalForm, accountData },
});

export const closeFormModal = (): TAction => ({
  type: EActionType.MODAL_CLOSE,
});

export const updateEmail = (
  modalForm: TModalForm,
  accountData: TAccountData
): TAction => ({
  type: EActionType.CHANGE_EMAIL,
  payload: { modalForm, accountData },
});

export const setCloseButtonVisibility = (
  closeButtonVisible: boolean
): TAction => ({
  type: EActionType.SET_CLOSE_BUTTON_VISIBILITY,
  payload: { closeButtonVisible },
});

export const onFetchFailure = (): TAction => ({
  type: EActionType.FETCH_FAILURE,
});

export const profileDataSet = (profileData: IProfileData): TAction => ({
  type: EActionType.PROFILE_DATA_SET,
  payload: { profileData },
});

export const profileDataUpdate = (
  path: (string | TProfileData)[],
  value: string | boolean
): TAction => ({
  type: EActionType.PROFILE_DATA_UPDATE,
  payload: { profileDataUpdate: { path, value } },
});

export const emailNotificationDataUpdate = (value: boolean): TAction => ({
  type: EActionType.EMAIL_NOTIFICATION_DATA_UPDATE,
  payload: { emailNotificationData: { newsletter_enabled: value } },
});
export const emailNotificationDataSync = (): TAction => ({
  type: EActionType.EMAIL_NOTIFICATION_DATA_SYNC,
});
export const setModalError = (error: string): TAction => ({
  type: EActionType.SET_MODAL_ERROR,
  payload: { modalErrorMessage: error },
});
